import dayjs from 'dayjs';
import cs from 'classnames';
import { Space, Table, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { useRequestsContext } from '../context/requests.ctx';
import { getStatusText } from '../utils/requests.util';
import { IRequest } from '../../../interfaces/requests.interface';
import { Actions } from './Actions';
import { AssignModal } from './assign-driver';
import { OrderSearch } from './order-search';
import { useWorkspaceStore } from '../../../store/workspace.store';

const { Text } = Typography;

const TableRequests = ({ columnsSelected }: { columnsSelected?: string[] }) => {
    const { t } = useTranslation('small-fleet-orders');
    const { t: tb } = useTranslation('bookings', { keyPrefix: 'TABLE' });

    const workspace = useWorkspaceStore((state) => state.data);

    const {
        data,
        loading,
        pagination,
        total,
        assignDriver,
        viewSearch,
        onPageChange,
        setAssignDriver,
        setViewSearch,
    } = useRequestsContext();

    const columns = [
        {
            title: tb('DATE'),
            dataIndex: 'createdAt',
            key: 'createdAt',
            align: 'center' as 'center',
            render: (createdAt: Date) => (
                <Space direction="vertical" size={4}>
                    <Text>
                        {dayjs(createdAt).format('DD MMM YYYY')}
                    </Text>
                    <Text>
                        {dayjs(createdAt).format('HH:mm')}
                    </Text>
                </Space>
            ),
        },
        {
            title: tb('SERVICE_ID'),
            dataIndex: 'bookingNumericId',
            key: 'bookingNumericId',
            align: 'center' as 'center',
            render: (bookingNumericId: number) => (
                <Text>
                    {bookingNumericId}
                </Text>
            ),
        },
        {
            title: tb('STORE'),
            dataIndex: 'businessName',
            key: 'businessName',
            align: 'center' as 'center',
            render: (businessName: string) => (
                <Text>
                    {businessName}
                </Text>
            ),
        },
        {
            title: tb('CUSTOMER'),
            dataIndex: 'customerName',
            key: 'customerName',
            align: 'center' as 'center',
            render: (customerName: string) => (
                <Text>
                    {customerName}
                </Text>
            ),
        },
        {
            title: tb('STATUS'),
            dataIndex: 'status',
            key: 'status',
            align: 'center' as 'center',
            render: (status: string, record: IRequest) => {
                const statusText = getStatusText(status, workspace?.keyName as string, record.providerName as string);
                return (
                    <Text className={cs('table-status', statusText)}>
                        {t(statusText)}
                    </Text>
                );
            },
        },
        {
            title: tb('ACTIONS'),
            dataIndex: 'actions',
            key: 'actions',
            align: 'center' as 'center',
            width: 100,
            render: (actions: string, record: IRequest) => (
                <Actions
                    record={record}
                    setAssignDriver={setAssignDriver}
                    setViewSearch={setViewSearch}
                />
            ),
        },
    ];

    const visibleColumns = columnsSelected
        ? columns.filter((col) => col.key === 'actions' || columnsSelected.includes(col.key))
        : columns;

    return (
        <>
            {Boolean(assignDriver) && <AssignModal request={assignDriver as IRequest | null} />}
            {Boolean(viewSearch) && <OrderSearch request={viewSearch as IRequest | null} />}
            <Table
                className="table"
                dataSource={data}
                columns={visibleColumns}
                scroll={{ x: 1000 }}
                locale={{
                    filterConfirm: 'Ok',
                    filterReset: 'Reset',
                    emptyText: tb('NO_DATA'),
                }}
                pagination={{
                    current: pagination.page,
                    total,
                    onChange: (page: number, pageSize: number) => onPageChange(page, pageSize),
                    pageSize: pagination.limit,
                    showSizeChanger: true,
                }}
                rowKey="_id"
                loading={loading}
                onRow={() => ({
                    onClick: () => {},
                })}
            />
        </>
    );
};

export { TableRequests };
